import { useLocalStorage } from '/src/composables/useLocalStorage.js';

/**
 * LocalStorageAdapter - Adapts useLocalStorage to persist plugin contract
 *
 * This adapter translates the persist plugin's contract (get, set, remove, clear)
 * to the full localStorage API provided by useLocalStorage composable.
 *
 * @returns {Object} Persist plugin storage adapter
 */
export function createLocalStorageAdapter () {
	const { getItem, setItem, removeItem, clear } = useLocalStorage();

	return {
		/**
         * Get a value by key (persist plugin contract)
         * @param {Object} params
         * @param {string} params.key - The key to retrieve
         * @returns {Promise<{value: string}>} Object with value property
         * @throws {Error} If key not found
         */
		async get ({ key }) {
			const value = await getItem(key);

			if (value === null) {
				throw new Error(`Key "${key}" not found in localStorage`);
			}

			return { value };
		},

		/**
         * Set a value by key (persist plugin contract)
         * @param {Object} params
         * @param {string} params.key - The key to set
         * @param {string} params.value - The value to store (already serialized)
         * @returns {Promise<void>}
         */
		async set ({ key, value }) {
			await setItem(key, value);
		},

		/**
         * Remove a value by key (persist plugin contract)
         * @param {Object} params
         * @param {string} params.key - The key to remove
         * @returns {Promise<void>}
         */
		async remove ({ key }) {
			await removeItem(key);
		},

		/**
         * Clear all values (persist plugin contract)
         * @returns {Promise<void>}
         */
		async clear () {
			await clear();
		},
	};
}
